import React, { useEffect, useMemo, useState } from 'react';
import { getConfigContable, setConfigContable, getPUC } from '../services/api';

const SECCIONES = [
  {
    titulo: 'Cartera y disponible',
    nota: 'Cuentas para registrar cobros y movimientos de tesorería.',
    campos: [
      { key: 'cuenta_clientes',   label: 'Clientes (cartera)',         hint: 'Default 130505' },
      { key: 'cuenta_caja',       label: 'Caja',                       hint: 'Default 110505' },
      { key: 'cuenta_banco',      label: 'Banco',                      hint: 'Default 111005' },
    ],
  },
  {
    titulo: 'Retenciones a favor (cuando nos retienen)',
    nota: 'Anticipos de impuesto que aparecen como activo cuando un cliente nos descuenta retenciones en sus pagos.',
    campos: [
      { key: 'cuenta_retefuente_a_favor', label: 'Retención en la fuente', hint: '135515' },
      { key: 'cuenta_reteiva_a_favor',    label: 'ReteIVA',                hint: '135517' },
      { key: 'cuenta_reteica_a_favor',    label: 'ReteICA',                hint: '135518' },
    ],
  },
  {
    titulo: 'Pasivos — proveedores e impuestos',
    campos: [
      { key: 'cuenta_proveedores',     label: 'Proveedores',          hint: '220505' },
      { key: 'cuenta_iva_generado',    label: 'IVA generado (FE)',    hint: '240805' },
      { key: 'cuenta_iva_descontable', label: 'IVA descontable',      hint: '240810' },
      { key: 'cuenta_inc_por_pagar',   label: 'INC por pagar',        hint: '243605' },
    ],
  },
  {
    titulo: 'Retenciones por pagar (cuando retenemos a proveedores)',
    campos: [
      { key: 'cuenta_retefuente_por_pagar', label: 'Retefuente por pagar', hint: '236525' },
      { key: 'cuenta_reteiva_por_pagar',    label: 'ReteIVA por pagar',    hint: '236701' },
      { key: 'cuenta_reteica_por_pagar',    label: 'ReteICA por pagar',    hint: '236805' },
    ],
  },
  {
    titulo: 'Ingresos',
    campos: [
      { key: 'cuenta_ingresos_servicios', label: 'Ingresos por servicios', hint: '417505' },
      { key: 'cuenta_ingresos_bienes',    label: 'Ingresos por bienes',    hint: '413505' },
    ],
  },
  {
    titulo: 'Costos',
    campos: [
      { key: 'cuenta_costo_servicios', label: 'Costo de servicios', hint: '617505' },
      { key: 'cuenta_costo_bienes',    label: 'Costo de bienes',    hint: '613505' },
    ],
  },
];

export default function ConfiguracionContable() {
  const [cuentas, setCuentas] = useState([]);
  const [config, setConfig]   = useState(null);
  const [error, setError]     = useState('');
  const [info, setInfo]       = useState('');
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [puc, cfg] = await Promise.all([
          getPUC({ activa: true }), getConfigContable(),
        ]);
        setCuentas(puc.data.filter(c => c.acepta_movimiento));
        setConfig(cfg.data || {});
      } catch (err) {
        setError(err.response?.data?.error || 'Error al cargar configuración');
      }
    })();
  }, []);

  const setCampo = (key, val) => setConfig(c => ({ ...c, [key]: val || null }));

  const guardar = async () => {
    setError(''); setInfo(''); setSaving(true);
    try {
      const payload = {};
      SECCIONES.forEach(s => s.campos.forEach(f => { payload[f.key] = config[f.key] || null; }));
      const { data } = await setConfigContable(payload);
      setConfig(data);
      setInfo('✓ Configuración guardada');
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const opciones = useMemo(() => cuentas.map(c => (
    <option key={c.codigo} value={c.codigo}>{c.codigo} — {c.nombre}</option>
  )), [cuentas]);

  if (!config) return <div style={S.container}>Cargando…</div>;

  return (
    <div style={S.container}>
      <div style={S.header}>
        <h1 style={S.title}>Configuración contable</h1>
        <p style={S.sub}>
          Define qué cuenta del PUC usar para cada concepto. Estos defaults
          alimentan los asientos automáticos al aprobar facturas y registrar pagos.
        </p>
      </div>

      {error && <div style={S.error}>{error}</div>}
      {info  && <div style={S.info}>{info}</div>}

      {SECCIONES.map(s => (
        <div key={s.titulo} style={S.seccion}>
          <h3 style={S.seccionTitle}>{s.titulo}</h3>
          {s.nota && <p style={S.seccionNota}>{s.nota}</p>}
          <div style={S.grid}>
            {s.campos.map(f => (
              <div key={f.key}>
                <label style={S.label}>{f.label}</label>
                <select
                  style={S.input}
                  value={config[f.key] || ''}
                  onChange={e => setCampo(f.key, e.target.value)}
                >
                  <option value="">— sin asignar —</option>
                  {opciones}
                </select>
                {f.hint && <span style={S.hint}>Sugerido: {f.hint}</span>}
              </div>
            ))}
          </div>
        </div>
      ))}

      <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
        <button onClick={guardar} disabled={saving} style={S.btnPri}>
          {saving ? 'Guardando…' : 'Guardar cambios'}
        </button>
      </div>
    </div>
  );
}

const S = {
  container: { padding: 24, maxWidth: 1100, margin: '0 auto' },
  header:    { marginBottom: 18 },
  title:     { fontSize: 24, fontWeight: 700, color: '#0f172a', margin: 0 },
  sub:       { color: '#64748b', margin: '4px 0 0', fontSize: 13, maxWidth: 700 },
  error:     { background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 },
  info:      { background: '#dcfce7', border: '1px solid #86efac', color: '#166534', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 },
  seccion:      { background: '#fff', borderRadius: 8, padding: 18, marginBottom: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  seccionTitle: { margin: '0 0 4px', fontSize: 15, color: '#0f172a' },
  seccionNota:  { margin: '0 0 12px', fontSize: 12, color: '#64748b', maxWidth: 600 },
  grid:         { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 },
  label:        { display: 'block', fontSize: 13, color: '#475569', marginBottom: 4, fontWeight: 500 },
  input:        { width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', background: '#fff' },
  hint:         { display: 'block', fontSize: 11, color: '#94a3b8', marginTop: 3 },
  btnPri:       { padding: '10px 20px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 600 },
};
